<template>
	<div class="icd-item">
		<v-list-item style="border: 1px solid #e0e0e0; border-radius: 4px">
			<v-list-item-content>
				<v-list-item-title class="pb-2">
					<span class="primary--text">{{icdSymptom.title}}</span>
					<template v-for="(active_extension, i) in icdSymptom.active_extensions">
						<v-icon color="primary" small :key="i + active_extension.title">mdi-chevron-right</v-icon>
						<span class="primary--text" :key="active_extension.title">{{active_extension.title}}</span>
					</template>
					<v-btn icon small class="mx-1" :disabled="!editable" @click="showDetails = !showDetails">
						<v-icon v-if="!showDetails" color="primary">mdi-chevron-down</v-icon>
						<v-icon v-else color="primary">mdi-chevron-up</v-icon>
					</v-btn>
					<span class="font-weight-bold text-caption">{{icdSymptom.code}}</span>
					<template v-for="(active_extension, i) in icdSymptom.active_extensions">
						<span class="text-caption mx-1" :key="i">+</span>
						<span class="font-weight-bold text-caption" :key="active_extension.code">{{active_extension.code}}</span>
					</template>
					<template v-if="context == 'test-results'">
						<v-btn v-if="!added" class="float-right" icon small @click="addICD">
							<v-icon color="grey lighten-1">mdi-check-circle-outline</v-icon>
						</v-btn>
						<v-btn v-else class="float-right" icon small @click="removeAddedICD">
							<v-icon color="primary" class="float-right pa-1">mdi-close-circle</v-icon>
						</v-btn>
					</template>
					<v-btn v-else-if="editable && context && context != 'admin'" class="float-right" :disabled="disabled" icon small @click="removeICD(icdSymptom)" :loading="deleting">
						<v-icon color="grey lighten-1">mdi-close</v-icon>
					</v-btn>
					<template v-if="context == 'admin'">
						<v-btn v-if="!isExtension" class="float-right" icon :to="{name: 'edit-icd', params: {iid: icdSymptom.disease_id}}">
							<v-icon color="primary">edit</v-icon>
						</v-btn>
						<v-btn v-else class="float-right" icon :to="{name: 'edit-extension', params: {eid: icdSymptom.id}}">
							<v-icon color="primary">edit</v-icon>
						</v-btn>
					</template>
					<template v-if="context == 'appointment'">
						<v-btn class="float-right" v-if="editable" :disabled="disabled" icon small @click="showMonitoringInfo = true">
							<v-icon :color="monitorColor">mdi-chart-line</v-icon>
						</v-btn>
						<v-rating
							v-if="showIntensity"
							small
							style="width: 50px"
							v-model="icdSymptom.intensity"
							length="6"
							empty-icon="mdi-checkbox-blank-circle-outline"
							full-icon="mdi-checkbox-blank-circle"
							hover
							dense
							:color="intensityColor"
							background-color="secondary"
							@input="saveIntensity"
							:readonly="disabled"
						></v-rating>
						<v-dialog v-model="showMonitoringInfo" width="70%">
							<v-card>
								<v-card-title class="title primary--text mb-2 secondary">
									<v-icon class="mr-3" color="primary">mdi-chart-line</v-icon>
									<translate>Monitor Symptom</translate>
									<span class="mx-1">-</span>
									{{icdSymptom.title}}
								</v-card-title>
								<v-card-text>
									<MonitoredSymptomList
										v-if="monitoredSymptoms.length > 0"
										:monitoredSymptoms="monitoredSymptoms"
									/>
									<v-alert v-model="monitoringNotShared" color="secondary" icon="info" class="text-caption">
										<translate>This symptom is being monitored by other professionals in your space. You can ask them to share the monitoring or you can create you own.</translate>
									</v-alert>
									<MonitoredSymptomForm
										v-if="allowMonitoredSymptomCreation"
										ref="MSForm"
										@monitored-symptom-created="mSCreated"
										:patientId="$route.params.pid"
										:symptom="{'disease': icdSymptom}"
									/>
								</v-card-text>
								<v-divider></v-divider>
								<v-card-actions>
									<v-spacer></v-spacer>
									<v-btn
										v-if="allowMonitoredSymptomCreation"
										color="primary darken-1"
										text
										@click="createMonitoredSymptom"
										:disabled="!$store.getters.hasAnyModule"
									>
										<translate>Create</translate>
									</v-btn>
									<template v-if="!allowMonitoredSymptomCreation">
										<v-btn color="primary darken-1" text @click="$emit('open-send-monitoring-email')">
											<v-icon class="mr-2">mdi-email-edit-outline</v-icon>
											<translate key="send-link">Send Link</translate>
										</v-btn>
										<v-btn color="primary darken-1" v-clipboard:copy="monitoringLink" text>
											<v-icon class="mr-2">mdi-link-variant</v-icon>
											<translate key="copy-link">Copy Link</translate>
										</v-btn>
									</template>
									<v-btn color="primary darken-1" text @click="showMonitoringInfo = false">
										<translate key="close">Close</translate>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-dialog>
					</template>
					<v-rating
						v-if="context == 'new-product'"
						small
						style="width: 50px"
						v-model="icdSymptom.degree"
						length="10"
						empty-icon="mdi-checkbox-blank-circle-outline"
						full-icon="mdi-checkbox-blank-circle"
						hover
						dense
						color="primary"
						background-color="secondary"
					></v-rating>
				</v-list-item-title>
				<v-list-item-subtitle v-if="icdSymptom.chapter">
					{{icdSymptom.chapter.title}}
					<v-card
						class="d-inline-flex px-1 float-right"
						style="opacity: 0.6"
						:color="icdSymptom.chapter.color"
						outlined
					>
						<div class="text-caption white--text">
							<translate>Chapter</translate>
							{{icdSymptom.chapter.id}}
						</div>
					</v-card>
				</v-list-item-subtitle>
				<v-row v-if="showDetails" no-gutters>
					<v-col cols="12">
						<v-divider class="my-3"></v-divider>
					</v-col>
					<template v-if="!noInfo">
						<v-col cols="12" class="text-caption my-1" v-if="icdSymptom.inclusion_infos.length > 0">
							<span class="font-weight-bold mr-1" v-translate>Inclusion:</span>
							<span
								v-for="inclusion in icdSymptom.inclusion_infos"
								:key="inclusion.id"
								class="grey--text mr-1"
							>
								{{inclusion.title}}
								<span
									v-if="inclusion.disease_code"
									class="ml-1"
								>[{{inclusion.disease_code}}]</span>;
							</span>
						</v-col>
						<v-col cols="12" class="text-caption my-1" v-if="icdSymptom.exclusion_infos.length > 0">
							<span class="font-weight-bold mr-1" v-translate>Exclusion:</span>
							<span
								v-for="exclusion in icdSymptom.exclusion_infos"
								:key="exclusion.id"
								class="grey--text mr-1"
							>
								{{exclusion.title}}
								<span
									v-if="exclusion.disease_code"
									class="ml-1"
								>[{{exclusion.disease_code}}]</span>;
							</span>
						</v-col>
						<v-col cols="12" class="text-caption my-1" v-if="icdSymptom.extension_infos.length > 0">
							<span class="font-weight-bold mr-1" v-translate>Extension:</span>
							<span
								v-for="extension in icdSymptom.extension_infos"
								:key="extension.id"
								class="grey--text mr-1"
							>
								{{extension.title}}
								<span v-if="extension.code" class="ml-1">[{{extension.code}}]</span>;
							</span>
						</v-col>
						<v-col v-if="icdSymptom.description" cols="12" class="text-caption my-1">
							<span class="font-weight-bold mr-1" v-translate>Description:</span>
							<span class="grey--text">{{icdSymptom.description}}</span>
						</v-col>
						<v-col v-if="icdSymptom.active_extensions.length > 0" cols="12" class="text-caption">
							<v-divider class="my-3"></v-divider>
							<span class="font-weight-bold mr-1 my-1" v-translate>Extension Codes Added:</span>
							<v-col cols="6">
								<v-list>
									<v-list-item
										v-for="active_extension in icdSymptom.active_extensions"
										:key="active_extension.code"
										style="border: 1px solid #e0e0e0; border-radius: 5px"
									>
										<v-list-item-content class="py-0">
											<v-list-item-title class="text-caption">{{active_extension.title}} [{{active_extension.code}}]</v-list-item-title>
										</v-list-item-content>
										<v-list-item-action class="my-0">
											<v-btn icon @click="removeExtension(active_extension)">
												<v-icon color="primary" small>delete</v-icon>
											</v-btn>
										</v-list-item-action>
									</v-list-item>
								</v-list>
							</v-col>
						</v-col>
						<v-col v-if="context == 'appointment' || context == 'monitoring'" cols="6" class="text-caption my-1">
							<span class="font-weight-bold mr-1" v-translate>Extension Codes Search:</span>
							<v-autocomplete
								v-model="extension"
								:loading="loadingExtensions"
								:placeholder="$gettext('Search ICD-11')"
								:search-input.sync="search"
								:items="extensionOptions"
								:item-text="displayName"
								item-value="code"
								clearable
								return-object
								outlined
								no-filter
								hide-no-data
								dense
								class="mt-2 text-caption"
								hide-details
								@input="addExtension"
								:disabled="disabled"
							>
								<v-icon slot="prepend-inner" small class="mt-1">mdi-magnify</v-icon>
							</v-autocomplete>
						</v-col>
					</template>
					<template v-else>
						<p class="font-weight-thin text-caption ml-2 my-1" v-translate>No Information available.</p>
					</template>
				</v-row>
			</v-list-item-content>
		</v-list-item>
	</div>
</template>

<script>
import axios from "axios";
import MonitoredSymptomForm from "@/components/patient/MonitoredSymptomForm.vue";
import MonitoredSymptomList from "@/components/patient/MonitoredSymptomList.vue";

export default {
	name: "ICDItem",
	props: {
		icdSymptom: {},
		editable: {
			default: true
		},
		context: {
			default: "appointment"
		},
		patient: {
			default: () => ({})
		},
		showIntensity: {
			default: false
		},
		disabled: {
			default: false
		},
		isExtension: {
			default: false
		}
	},
	components: {
		MonitoredSymptomForm,
		MonitoredSymptomList
	},
	data: () => ({
		showDetails: false,
		showMonitoringInfo: false,
		loadingExtensions: false,
		extensionOptions: [],
		search: "",
		extension: null,
		monitoredSymptoms: [],
		beingMonitored: false,
		intensityColors: [
			"primary lighten-5",
			"primary lighten-4",
			"primary lighten-3",
			"primary lighten-2",
			"primary lighten-1",
			"primary"
		],
		added: false,
		deleting: false
	}),
	methods: {
		removeICD(icd) {
			this.deleting = true;
			this.$emit("remove-icd", icd);
		},
		addICD() {
			const appointmentId = this.$route.params.aid;
			let type = this.icdSymptom.chapter.id != 26 ? 'gen_diagnose' : 'mtc_diagnose';
			axios
				.post("/app/symptom", null, {
					params: {
						appointment: appointmentId,
						model: "ICD11",
						disease_id: this.icdSymptom.disease_id,
						action: "add",
						type: type,
						main: false
					}
				})
				.then(response => {
					console.log(response.data);
					if (response.data.result === "added") {
						this.added = true;
					}
				})
				.catch(error => console.log(error));
		},
		removeAddedICD() {
			const appointmentId = this.$route.params.aid;
			let type = this.icdSymptom.chapter.id != 26 ? 'gen_diagnose' : 'mtc_diagnose';
			axios
				.post("/app/symptom", null, {
					params: {
						appointment: appointmentId,
						model: "ICD11",
						disease_id: this.icdSymptom.disease_id,
						action: "delete",
						type: type,
						main: false
					}
				})
				.then(response => {
					console.log(response.data);
					if (response.data.result === "deleted") {
						this.added = false;
					}
				})
				.catch(error => console.log(error));
		},
		addExtension() {
			if (this.extension) {
				var exist = false;
				for (var i = 0; i < this.icdSymptom.active_extensions.length; i++) {
					if (
						this.icdSymptom.active_extensions[i].id == this.extension.id
					) {
						exist = true;
						break;
					}
				}
				if (!exist) {
					const extension = this.extension;
					if (this.context == 'monitoring') {
						this.icdSymptom.active_extensions.push(extension);
						this.extension = null;
						this.search = "";
						this.extensionOptions = [];
						return true;
					}
					const formData = new FormData();
					formData.append("extension_id", this.extension.id);
					formData.append("disease_id", this.icdSymptom.disease_id);
					formData.append("type", this.icdSymptom.type);
					formData.append("model", "ICD11");
					formData.append("appointment_id", this.$route.params.aid);
					axios
						.post("/extension-code/add", formData)
						.then(response => {
							console.log(response.data);
							if (response.data.result === "ok") {
								this.icdSymptom.active_extensions.push(extension);
							}
						})
						.catch(error => {
							console.log(error);
							this.extension = null;
							this.search = "";
							this.extensionOptions = [];
						});
					this.extension = null;
					this.search = "";
					this.extensionOptions = [];
				}
			}
		},
		removeExtension(extension) {
			if (this.context == 'monitoring') {
				this.icdSymptom.active_extensions.splice(this.icdSymptom.active_extensions.indexOf(extension),1);
				return true;
			}
			const formData = new FormData();
			formData.append("extension_id", extension.id);
			formData.append("disease_id", this.icdSymptom.disease_id);
			formData.append("type", this.icdSymptom.type);
			formData.append("model", "ICD11");
			formData.append("appointment_id", this.$route.params.aid);
			axios
				.post("/extension-code/remove", formData)
				.then(response => {
					console.log(response.data);
					if (response.data.result === "ok") {
						this.icdSymptom.active_extensions.splice(
							this.icdSymptom.active_extensions.indexOf(extension),
							1
						);
					}
				})
				.catch(error => {
					console.log(error);
				});
		},
		makeRequest: function(value, self) {
			clearTimeout(this._timerId);
			self.loadingExtensions = true;
			// delay new call 500ms
			this._timerId = setTimeout(() => {
				console.log("API CALL");
				axios
					.get("/extension-codes/search", {
						params: { value: value, disease_id: this.icdSymptom.disease_id, page: 1 }
					})
					.then(response => {
						console.log(response.data);
						this.extensionOptions = response.data;
						self.loadingExtensions = false;
					})
					.catch(error => {
						console.log(error);
						self.loadingExtensions = false;
					});
			}, 300);
		},
		displayName(i) {
			return i.code + " - " + i.title;
		},
		createMonitoredSymptom() {
			console.log("CREATING");
			this.$refs.MSForm.createMonitoredSymptom();
		},
		mSCreated() {
			this.showMonitoringInfo = false;
			this.beingMonitored = true;
			setTimeout(() => {this.showMonitoringInfo = true;}, 10);
		},
		saveIntensity() {
			console.log("CHANGE INTENSITY");
			console.log(this.icdSymptom.intensity);
			const formData = new FormData();
			formData.append("disease_id", this.icdSymptom.disease_id);
			formData.append("type", this.icdSymptom.type);
			formData.append("model", "ICD11");
			formData.append("appointment_id", this.$route.params.aid);
			formData.append("intensity", this.icdSymptom.intensity);
			axios
				.post("/app/saveintensity", formData)
				.then(response => {
					console.log(response.data);
				})
				.catch(error => console.log(error));
		}
	},
	computed: {
		intensityColor() {
			return this.intensityColors[this.icdSymptom.intensity - 1];
		},
		noInfo() {
			if(this.icdSymptom.inclusion_infos.length == 0 && this.icdSymptom.exclusion_infos.length == 0 && this.icdSymptom.extension_infos.length == 0 && (this.icdSymptom.description == '' || this.icdSymptom.description == null)) return true;
			else return false;
		},
		monitorColor() {
			if (this.beingMonitored) return "primary";
			else return "grey lighten-1";
		},
		monitoringNotShared() {
			if (this.monitoredSymptoms.length > 0) {
				let monitoringNotShared = true;
				let userId = this.$store.getters.userId;
				for (const mS of this.monitoredSymptoms) {
					if (mS.user_id == userId) return false;
					for (const u of mS.shared_users) {
						if (u.id == userId) return false;
					}
				}
				return monitoringNotShared;
			} else return false;
		},
		allowMonitoredSymptomCreation() {
			for (const mS of this.monitoredSymptoms) {
				if (mS.user_id == this.$store.getters.userId) return false;
			}
			return true;
		},
		monitoringLink() {
			return (
				"app.naltio.com/monitoring/" +
				this.patient.id +
				"?key=" +
				this.patient.monitoring_key
			);
		}
	},
	watch: {
		search(value) {
			if (!value) {
				this.extensionOptions = [];
				return;
			}

			this.extensionOptions = [];
			this.makeRequest(value, this);
		},
		showMonitoringInfo(value) {
			if (value) {
				axios
					.get(
						"/patient/" +
							this.$route.params.pid +
							"/monitored_symptoms/" +
							this.icdSymptom.disease_id
					)
					.then(response => {
						console.log(response.data);
						this.monitoredSymptoms = response.data.monitored_symptoms;
						if(this.monitoredSymptoms.length > 0) this.beingMonitored = true;
					})
					.catch(error => console.log(error));
			}
		}
	}
};
</script>